import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    IProcessorDetail,
    ProcessorCreatePayload,
    ProcessorRestoreDeletedPayload,
    ProcessorUpdatePayload,
} from "@/customTypings/processor/Processor";
import { authHeader, handleResponse } from "../../utils";
import { postProcessorCreate } from "@/utils/processor/processorCreateConflict";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";

function siteProcessorsBase(siteId: string) {
    return `/api/sites/${encodeURIComponent(siteId)}/processors`;
}

export const processorsService = {
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function getById(siteId: string, id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`${siteProcessorsBase(siteId)}/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<IProcessorDetail>;
}

function create(siteId: string, payload: ProcessorCreatePayload) {
    return postProcessorCreate<IProcessorDetail>(siteId, payload);
}

function restoreDeletedWithCreateValues(siteId: string, payload: ProcessorRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`${siteProcessorsBase(siteId)}/restore-deleted-with-create-values`, requestOptions).then(
        handleResponse
    ) as Promise<IProcessorDetail>;
}

function update(siteId: string, payload: ProcessorUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`${siteProcessorsBase(siteId)}/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<IProcessorDetail>>;
}

function disable(siteId: string, id: string) {
    return postQuery(siteId, "disable", id);
}

function enable(siteId: string, id: string) {
    return postQuery(siteId, "enable", id);
}

function softDelete(siteId: string, id: string) {
    return postQuery(siteId, "softdelete", id);
}

function restore(siteId: string, id: string) {
    return postQuery(siteId, "restore", id);
}

function postQuery(siteId: string, action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`${siteProcessorsBase(siteId)}/${action}?id=${encodeURIComponent(id)}`, requestOptions).then(
        handleResponse
    );
}
